import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { getWorkouts } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Plus, CheckCircle, Circle, Clock, MapPin, Calendar } from 'lucide-react';

interface Workout {
  id: string;
  type: string;
  date: string;
  duration_minutes: number;
  distance_km: number;
  notes: string;
  completed: boolean;
}

const WorkoutList: React.FC = () => {
  const { planId } = useParams<{ planId: string }>();
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const { user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const fetchWorkouts = async () => {
      if (planId && user) {
        try {
          const data = await getWorkouts(user, planId);
          setWorkouts(data);
        } catch (error) {
          console.error('Error fetching workouts:', error);
        }
      }
    };

    fetchWorkouts();
  }, [planId, user]);

  const getTypeIcon = (type: string): string => {
    const icons: Record<string, string> = {
      swim: '🏊',
      bike: '🚴',
      run: '🏃',
      strength: '💪',
    };
    return icons[type.toLowerCase()] || '⚫';
  };

  return (
    <div>
      <button
        onClick={() => navigate(`/plans/${planId}`)}
        className="flex items-center space-x-2 text-muted hover:text-[#3b82f6] mb-6 transition-colors"
      >
        <ArrowLeft size={18} />
        <span>Back to Plan</span>
      </button>

      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold mb-2">Workouts</h1>
          <p className="text-muted">Track your training sessions</p>
        </div>
        <Link
          to={`/workouts/new/${planId}`}
          className="flex items-center space-x-2 px-4 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#60a5fa] transition-colors no-underline"
        >
          <Plus size={18} />
          <span>Log New Workout</span>
        </Link>
      </div>

      {workouts.length === 0 ? (
        <div className="card-base p-8 text-center">
          <p className="text-xl text-muted mb-4">No workouts logged yet</p>
          <Link
            to={`/workouts/new/${planId}`}
            className="inline-flex items-center space-x-2 px-4 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#60a5fa] transition-colors no-underline"
          >
            <Plus size={18} />
            <span>Log Your First Workout</span>
          </Link>
        </div>
      ) : (
        <div className="space-y-4">
          {workouts.map(workout => (
            <div
              key={workout.id}
              className={`card-base p-6 flex items-center justify-between ${workout.completed ? 'opacity-75' : ''}`}
            >
              <div className="flex items-center space-x-4">
                <div className="text-3xl">{getTypeIcon(workout.type)}</div>
                <div>
                  <h3 className="text-lg font-semibold capitalize">{workout.type}</h3>
                  <div className="flex items-center space-x-4 text-sm text-[#718096] mt-1">
                    <span className="flex items-center space-x-1">
                      <Calendar size={14} />
                      <span>{workout.date}</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <Clock size={14} />
                      <span>{workout.duration_minutes} min</span>
                    </span>
                    <span className="flex items-center space-x-1">
                      <MapPin size={14} />
                      <span>{workout.distance_km} km</span>
                    </span>
                  </div>
                </div>
              </div>
              <div className="flex items-center space-x-4">
                {workout.completed ? (
                  <CheckCircle className="text-[#10b981]" size={24} />
                ) : (
                  <Circle className="text-[#718096]" size={24} />
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WorkoutList;
